/**
 * Template compilation and cache ownership.
 *
 * Hand-authored tagged templates are compiled once per strings identity.
 * Compiler-emitted templates are registered once per stable id. Both paths
 * normalize into the same static HTML shell and exhaustive part-spec list.
 * Attribute context is detected before a marker is inserted so dynamic values
 * never reopen the HTML parser. Compile-position guards (RT402-RT405) reject
 * interpolations HTML cannot represent — comments, raw-text element content,
 * dynamic tag/attribute names, and multi-expression non-plain-attributes —
 * before any marker is emitted, so a malformed template never reaches the
 * renderers. DOM materialization and string rendering then consume this
 * normalized representation independently.
 */
import { isSvgTitle } from './render-template-namespace.js';
import { elideFormattingWhitespace } from './render-template-whitespace.js';
import { attrMarker, elementMarker, isAttributePartType, partMarker, runtimeError, } from './render-values.js';
const templateCache = new WeakMap();
/**
 * `svg` templates cache separately from `html` (G12): the same strings
 * identity can legitimately compile once per tag when a call site is shared
 * through a helper, and the two compiled results must never collide — one
 * carries `namespace: 'svg'`, the other doesn't.
 */
const svgTemplateCache = new WeakMap();
const registeredTemplates = new Map();
/** Raw-text elements: content is never re-tokenized as markup by the HTML parser. */
const RAW_TEXT_TAGS = ['textarea', 'script', 'style', 'title'];
/** Interpolation directly extending an unclosed `<` / `</` tag-name run. */
const TAG_NAME_POSITION_RE = /<\/?[A-Za-z0-9-]*$/;
/** A prior compiler marker already sits inside the currently open attribute value. */
const COMPOSITE_MARKER_RE = /__h:\d+__/;
export function normalizeAttributeName(name) {
    if (name.startsWith('@') || name.startsWith('.') || name.startsWith('?')) {
        return name.slice(1);
    }
    return name;
}
function eventNameFromAttribute(name) {
    if (name.startsWith('@'))
        return name.slice(1);
    if (name.startsWith('on') && name.length > 2)
        return name.slice(2);
    return name;
}
function normalizePartSpec(spec, index) {
    // Compiler inputs are intentionally loose at this boundary. Normalization
    // supplies stable indices, binding keys, and marker forms before either
    // renderer sees the template.
    const type = (spec.type || 'node');
    const resolvedIndex = spec.index ?? index;
    const binding = spec.binding ?? spec.name ?? spec.index ?? index;
    const base = {
        index: resolvedIndex,
        binding,
        ...(spec.name !== undefined ? { name: spec.name } : {}),
    };
    if (isAttributePartType(type)) {
        // Attribute-family parts retain both authored and normalized names because
        // DOM cleanup needs the authored prefix while updates use the real name.
        const rawName = spec.rawName || spec.name || spec.attrName || '';
        const attrName = spec.attrName || normalizeAttributeName(rawName);
        const marker = spec.marker || attrMarker(resolvedIndex);
        if (type === 'event') {
            const eventName = spec.eventName || eventNameFromAttribute(rawName) || attrName;
            return {
                ...base,
                type: 'event',
                marker,
                rawName,
                attrName,
                eventName,
            };
        }
        if (type === 'attribute') {
            // Composite attributes (`attr="a ${x} b ${y}"`) carry every contributing
            // binding index; single-hole attributes never set this field.
            const bindingIndices = Array.isArray(spec.bindingIndices)
                ? spec.bindingIndices.map((value) => Number(value))
                : undefined;
            return {
                ...base,
                type: 'attribute',
                marker,
                rawName,
                attrName,
                ...(bindingIndices ? { bindingIndices } : {}),
            };
        }
        return {
            ...base,
            type,
            marker,
            rawName,
            attrName,
        };
    }
    const marker = spec.marker || partMarker(resolvedIndex);
    switch (type) {
        // Node-family parts all bind to comment markers. Unknown future values
        // preserve the historical node fallback rather than failing compilation.
        case 'raw-html': // ubs:ignore -- this case returns, so no break is required
            return { ...base, type: 'raw-html', marker };
        case 'conditional': // ubs:ignore -- this case returns, so no break is required
            return {
                ...base,
                type: 'conditional',
                marker,
                ...(spec.truthy !== undefined ? { truthy: spec.truthy } : {}),
                ...(spec.falsy !== undefined ? { falsy: spec.falsy } : {}),
            };
        case 'repeat': // ubs:ignore -- this case returns, so no break is required
            return { ...base, type: 'repeat', marker };
        case 'keyed-sequence': // ubs:ignore -- this case returns, so no break is required
            return { ...base, type: 'keyed-sequence', marker };
        case 'text': // ubs:ignore -- this case returns, so no break is required
            return { ...base, type: 'text', marker };
        case 'node': // ubs:ignore -- this case returns, so no break is required
            return { ...base, type: 'node', marker };
        case 'element': // ubs:ignore -- this case returns, so no break is required
            // Element parts have no comment marker; they bind to a valueless
            // attribute name and must fall back to elementMarker, not partMarker.
            return { ...base, type: 'element', marker: spec.marker || elementMarker(resolvedIndex) };
        default:
            return { ...base, type: 'node', marker };
    }
}
/**
 * Normalizes an already-lowered static shell and its part specs into a
 * `Template`. The static HTML arrives in its final form: authoring indentation
 * is dropped by `compileTaggedTemplate`, which is the only place authored
 * markup exists, and `registerTemplate`'s input has already been through that
 * lowering at build time. Doing it on the static HTML rather than on a parsed
 * clone is what keeps the DOM path and the string path describing the same
 * tree, and what keeps the cost at one pass per compile instead of one per
 * clone — or, for an AOT-emitted template, at none.
 */
function createTemplate(id, staticHtml, partSpecs, namespace, nav) {
    return {
        id,
        staticHtml,
        partSpecs: partSpecs.map(normalizePartSpec),
        domTemplate: null,
        // Assigned here rather than left absent so every Template keeps one hidden
        // class from creation through its first mount (#1375).
        liveInstances: 0,
        ...(namespace ? { namespace } : {}),
        ...(nav ? { nav } : {}),
    };
}
function isInsideOpenTag(html) {
    return html.lastIndexOf('<') > html.lastIndexOf('>');
}
function attributePartType(rawName) {
    if (rawName.startsWith('@') || /^on[a-z]/i.test(rawName))
        return 'event';
    if (rawName.startsWith('.'))
        return 'property';
    if (rawName.startsWith('?'))
        return 'boolean-attribute';
    return 'attribute';
}
function readAttributeContext(html) {
    if (!isInsideOpenTag(html))
        return null;
    // Only a trailing attribute assignment can own the next interpolation.
    // Earlier attributes and closed tags are deliberately ignored. The three
    // alternatives capture whatever value text has already been typed for a
    // double-quoted, single-quoted, or bare assignment so composite detection
    // can inspect it without re-deriving the match.
    const match = html.match(/([^\s"'<>/=]+)\s*=\s*(?:"([^"]*)$|'([^']*)$|([^\s"'=<>`]*)$)/);
    if (!match)
        return null;
    const rawName = match[1] ?? '';
    const type = attributePartType(rawName);
    const valueFragment = match[2] ?? match[3] ?? match[4] ?? '';
    return {
        type,
        rawName,
        attrName: normalizeAttributeName(rawName),
        eventName: type === 'event' ? eventNameFromAttribute(rawName) : null,
        valueFragment,
    };
}
/** A `<` that opens a genuine element tag (not a comment/doctype/PI). */
const TAG_OPEN_TRIGGER_RE = /[A-Za-z/]/;
/**
 * Replaces the contents of quoted attribute values with spaces so the
 * RT402/RT403 scans below never mistake markup-like text living INSIDE an
 * attribute value (e.g. `title="note: <!-- not a real comment"` or
 * `title="See the <textarea> element"`) for real structure — quotes suppress
 * HTML parsing entirely, so `<!--`/`-->`/`<textarea>` occurring inside one
 * are just characters, not markup. Quote tracking only activates once a `<`
 * has opened a genuine element tag (a letter or `/` immediately follows);
 * apostrophes in ordinary text and quotes inside comments/doctypes are left
 * untouched, so this stays a cheap linear scan rather than a full parse.
 */
function maskQuotedAttributeValues(html) {
    let result = '';
    let inTag = false;
    let quote = null;
    for (let i = 0; i < html.length; i += 1) {
        const ch = html[i] ?? '';
        if (quote) {
            result += ch === quote ? ch : ' ';
            if (ch === quote)
                quote = null;
            continue;
        }
        if (inTag) {
            if (ch === '"' || ch === "'") {
                quote = ch;
            }
            else if (ch === '>') {
                inTag = false;
            }
            result += ch;
            continue;
        }
        if (ch === '<' && TAG_OPEN_TRIGGER_RE.test(html[i + 1] ?? '')) {
            inTag = true;
        }
        result += ch;
    }
    return result;
}
/** RT402: an unclosed `<!--` precedes this interpolation. */
function isCommentOpen(html) {
    return html.lastIndexOf('<!--') > html.lastIndexOf('-->');
}
/** RT403: the last complete raw-text opening tag of `tag` has no closing tag yet. */
function openRawTextTag(html) {
    for (const tag of RAW_TEXT_TAGS) {
        const openTagPattern = new RegExp(`<${tag}(?:\\s[^>]*)?>`, 'gi');
        let lastOpenEnd = -1;
        for (let match = openTagPattern.exec(html); match; match = openTagPattern.exec(html)) {
            lastOpenEnd = match.index + match[0].length;
        }
        if (lastOpenEnd === -1)
            continue;
        const closeTagPattern = new RegExp(`</${tag}\\b`, 'i');
        if (!closeTagPattern.test(html.slice(lastOpenEnd)))
            return tag;
    }
    return null;
}
function rawTextGuardMessage(tag) {
    if (tag === 'textarea') {
        return ('Expressions inside <textarea> content are not supported — ' +
            'bind the value with <textarea .value=${...}> instead.');
    }
    return (`Expressions inside <${tag}> content are not supported — ` +
        'remove the ${...} or move it outside the element.');
}
/** Extends the most recently pushed attribute spec into a composite (G4). */
function extendCompositeSpec(partSpecs, index) {
    const previous = partSpecs[partSpecs.length - 1];
    if (!previous)
        return;
    const priorIndices = Array.isArray(previous.bindingIndices)
        ? previous.bindingIndices
        : typeof previous.index === 'number'
            ? [previous.index]
            : [];
    previous.bindingIndices = [...priorIndices, index];
}
/** Emits the marker for a plain/property/boolean/event attribute-family hole. */
function appendAttributeMarker(staticHtml, index, attrContext, partSpecs) {
    const isComposite = COMPOSITE_MARKER_RE.test(attrContext.valueFragment);
    if (isComposite && attrContext.type !== 'attribute') {
        throw runtimeError('RT405', 'property/boolean/event bindings accept exactly one ${...} expression');
    }
    if (isComposite) {
        extendCompositeSpec(partSpecs, index);
    }
    else {
        partSpecs.push({
            ...attrContext,
            index,
            binding: index,
            marker: attrMarker(index),
        });
    }
    return staticHtml + attrMarker(index);
}
/** Emits a bare element-part marker as a valueless attribute name (G1). */
function appendElementMarker(staticHtml, index, partSpecs) {
    const separator = /\s$/.test(staticHtml) ? '' : ' ';
    const marker = elementMarker(index);
    partSpecs.push({ type: 'element', index, binding: index, marker });
    return `${staticHtml}${separator}${marker}`;
}
/** Emits an ordinary child-content node-part comment marker. */
function appendNodeMarker(staticHtml, index, partSpecs) {
    partSpecs.push({ type: 'node', index, binding: index, marker: partMarker(index) });
    return `${staticHtml}<!--${partMarker(index)}-->`;
}
/**
 * A bare trailing `<`/`</` (optionally with a partial name) is genuinely a
 * dynamic-tag-name attempt (RT404) only when it immediately follows a real
 * tag boundary — template start or a previously closed tag's `>` — with
 * NOTHING else in between. A real HTML tokenizer cannot distinguish this
 * from a stray `<` sitting in ordinary text content (e.g. `5 < ${x}`) any
 * more than this compiler can: both reach the identical "just saw `<`,
 * nothing typed yet" state. Rather than guess wrong and reject previously
 * valid templates, only the unambiguous case (nothing before the `<` but a
 * real boundary) throws; the ambiguous case falls back to a plain node part,
 * matching how a real HTML parser tolerates a lone `<` as character data.
 */
function isAtTagBoundary(html) {
    const match = TAG_NAME_POSITION_RE.exec(html);
    if (!match)
        return false;
    return match.index === 0 || html[match.index - 1] === '>';
}
function compileIdentity(strings) {
    return 'raw' in strings ? strings : '';
}
function compileTaggedTemplate(strings, namespace) {
    // Parsing is front-loaded so repeated instances clone cached static markup
    // instead of re-reading template string context on every mount.
    let staticHtml = '';
    const partSpecs = [];
    for (let index = 0; index < strings.length - 1; index += 1) {
        staticHtml += strings[index] ?? '';
        // Quoted attribute-value text can contain markup-like substrings
        // (`title="note: <!-- not a comment"`) that must never be mistaken for
        // real comment/raw-text structure — mask it once and scan the result.
        const masked = maskQuotedAttributeValues(staticHtml);
        if (isCommentOpen(masked)) {
            throw runtimeError('RT402', 'Expressions inside HTML comments are not supported — remove the ${...} or move it outside the comment.');
        }
        const rawTextTag = openRawTextTag(masked);
        if (rawTextTag && !(rawTextTag === 'title' && isSvgTitle(masked, namespace))) {
            throw runtimeError('RT403', rawTextGuardMessage(rawTextTag));
        }
        if (!isInsideOpenTag(staticHtml)) {
            staticHtml = appendNodeMarker(staticHtml, index, partSpecs);
            continue;
        }
        const attrContext = readAttributeContext(staticHtml);
        if (attrContext) {
            staticHtml = appendAttributeMarker(staticHtml, index, attrContext, partSpecs);
            continue;
        }
        // Not inside a `name=` assignment: either the tag/attribute name itself
        // is being interpolated (unsupported, RT404) or this is a bare element
        // part binding to the element as a whole.
        const nextStatic = strings[index + 1] ?? '';
        if (/^\s*=/.test(nextStatic)) {
            throw runtimeError('RT404', 'Dynamic tag and attribute names are not supported.');
        }
        if (TAG_NAME_POSITION_RE.test(staticHtml)) {
            if (isAtTagBoundary(staticHtml)) {
                throw runtimeError('RT404', 'Dynamic tag and attribute names are not supported.');
            }
            // Ambiguous bare '<'/'</' not at a real tag boundary: not actually
            // inside a tag after all (see isAtTagBoundary) — ordinary node part.
            staticHtml = appendNodeMarker(staticHtml, index, partSpecs);
            continue;
        }
        staticHtml = appendElementMarker(staticHtml, index, partSpecs);
    }
    staticHtml += strings[strings.length - 1] ?? '';
    // Authoring indentation is dropped once, here, on the assembled marker-bearing
    // shell. `compileHtmlStrings` shares this path, so an AOT-lowered template is
    // already elided by the time `registerTemplate` sees it and never pays for a
    // second pass at runtime — and an app that only registers templates never
    // links the eliding scanner at all.
    return createTemplate(compileIdentity(strings), elideFormattingWhitespace(staticHtml), partSpecs, namespace);
}
/**
 * Compile-time entry for AOT `html`/`svg` lowering. Same IR as runtime
 * `html()` — static HTML shell plus exhaustive part specs — without
 * caching against a TemplateStringsArray identity. The namespace travels
 * with the compilation so an AOT-registered `svg` template parses as SVG
 * foreign content exactly like the runtime `svg` tag's own compile (#1410).
 */
export function compileHtmlStrings(strings, namespace) {
    const template = compileTaggedTemplate(strings, namespace);
    return { staticHtml: template.staticHtml, partSpecs: template.partSpecs };
}
/**
 * Compiles (or returns the cached compilation of) a tagged template's
 * strings. `namespace: 'svg'` (G12) routes through a dedicated cache so the
 * same strings identity can never resolve to the wrong compiled template —
 * `html` and `svg` tags at a shared call site compile and cache
 * independently.
 */
export function templateForStrings(strings, namespace) {
    const cache = namespace === 'svg' ? svgTemplateCache : templateCache;
    let template = cache.get(strings);
    if (!template) {
        template = compileTaggedTemplate(strings, namespace);
        cache.set(strings, template);
    }
    return template;
}
export function registerTemplate(id, staticHtml, partSpecs, namespace, nav) {
    const existing = registeredTemplates.get(id);
    if (existing)
        return existing;
    const template = createTemplate(id, staticHtml, partSpecs, namespace, nav);
    registeredTemplates.set(id, template);
    return template;
}
function bindingAt(bindings, index) {
    if (Array.isArray(bindings))
        return bindings[index];
    if (bindings && typeof bindings === 'object')
        return bindings[index];
    return undefined;
}
export function getBinding(bindings, spec) {
    if (spec.type === 'attribute' && spec.bindingIndices) {
        // Array-bag path only: composites are never produced for named-bag
        // (compiler-emitted) templates.
        return spec.bindingIndices.map((partIndex) => bindingAt(bindings, partIndex));
    }
    if (Array.isArray(bindings)) {
        const key = spec.binding;
        if (typeof key === 'number')
            return bindings[key];
        const asNum = Number(key);
        return Number.isInteger(asNum) ? bindings[asNum] : undefined;
    }
    if (bindings && typeof bindings === 'object') {
        if (spec.binding in bindings)
            return bindings[spec.binding];
        if (spec.name !== undefined && spec.name in bindings) {
            return bindings[spec.name];
        }
        if (spec.index in bindings)
            return bindings[spec.index];
    }
    return undefined;
}
//# sourceMappingURL=render-template.js.map